import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f54be32c9d1511a86132296a5b3958a7.jpg"
            alt="Financial Technology"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/65 to-black/75"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block bg-red-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
            ⚠️ WARNING: This Page Closes In 24 Hours
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl">
            If You Skip This Video,
          </h1>
          <p className="text-4xl md:text-6xl font-extrabold mb-8">
            <span className="text-amber-400">You'll Pay The Price...</span>{' '}
            <span className="text-amber-300">Full Price!!!</span>
          </p>
          <div className="bg-white/10 backdrop-blur-sm border-2 border-amber-400 rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-white text-xl md:text-2xl font-semibold">
              <span className="text-amber-400">97% of people</span> overpay for everything because they don't know this secret pricing layer exists
            </p>
          </div>
        </div>
      </section>

      {/* Urgency Banner */}
      <div className="bg-red-600 text-white py-3 text-center">
        <p className="text-sm md:text-base font-bold">
          🔥 <strong>LIMITED ACCESS:</strong> Only 247 spots remaining for this private video
        </p>
      </div>

      {/* Three Column Info Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-amber-400">
            The Truth About Pricing Nobody Tells You
          </h2>
          <p className="text-center text-gray-300 text-lg mb-12 max-w-3xl mx-auto">
            There are two worlds: those who pay retail, and those who don't. The difference isn't money—it's <strong className="text-amber-400">knowledge</strong>.
          </p>
          <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto items-stretch">
            <div className="bg-gray-900 p-8 rounded-lg border-2 border-amber-400/30 hover:border-amber-400 transition-all duration-300 flex flex-col">
              <div className="text-4xl mb-4">💡</div>
              <p className="text-amber-400 text-xl font-bold mb-4">
                This isn't about saving money.
              </p>
              <p className="text-gray-300 text-base leading-relaxed">
                It's about whether you understand how pricing actually works—and why some people <strong>never</strong> pay what you pay.
              </p>
            </div>
            
            <div className="bg-gray-900 p-8 rounded-lg border-2 border-amber-400/30 hover:border-amber-400 transition-all duration-300 flex flex-col">
              <div className="text-4xl mb-4">🎯</div>
              <p className="text-white text-lg leading-relaxed mb-3">
                Some people pay retail.
              </p>
              <p className="text-white text-lg leading-relaxed mb-3">
                Some people don't.
              </p>
              <p className="text-white text-lg leading-relaxed mb-4">
                The difference isn't income.
              </p>
              <p className="text-amber-400 text-3xl font-extrabold mt-6">
                It's ACCESS!
              </p>
            </div>
            
            <div className="bg-gray-900 p-8 rounded-lg border-2 border-amber-400/30 hover:border-amber-400 transition-all duration-300 flex flex-col">
              <div className="text-4xl mb-4">🔓</div>
              <p className="text-amber-400 text-xl font-bold mb-4">
                Have You Ever Wondered?
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-amber-400 mr-2">✓</span>
                  <span>Businesses don't pay what you pay</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-400 mr-2">✓</span>
                  <span>Insiders don't pay what you pay</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-400 mr-2">✓</span>
                  <span>Wholesale buyers don't pay what you pay</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 bg-gray-900 border-y-4 border-amber-400">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center">
            <div className="flex-1 min-w-[200px]">
              <div className="text-4xl font-bold text-amber-400">12,847+</div>
              <div className="text-gray-300 mt-2">People Unlocked This</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-4xl font-bold text-amber-400">$2.3M+</div>
              <div className="text-gray-300 mt-2">Saved Collectively</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-4xl font-bold text-amber-400">4.9/5</div>
              <div className="text-gray-300 mt-2">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Orange Section with Image and Lists */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-orange-600">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-black">
            What Are You're <span className="underline decoration-4 decoration-black">Really</span> Losing By Not Watching?
          </h2>
          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto items-stretch">
            <div className="bg-black/10 backdrop-blur-sm p-8 rounded-lg flex flex-col">
              <p className="text-2xl font-extrabold mb-6 text-black">
                This video costs $0 to watch.
              </p>
              <p className="text-lg mb-6 font-semibold text-black">
                But skipping it means you'll <strong>continue</strong> to:
              </p>
              <ul className="space-y-4 text-base text-black flex-1">
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="text-red-700 mr-3 text-xl font-bold">✗</span>
                  <span><strong>Overpay for travel</strong> (hotels, flights, rentals)</span>
                </li>
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="text-red-700 mr-3 text-xl font-bold">✗</span>
                  <span><strong>Overpay for everyday spending</strong> (groceries, gas, subscriptions)</span>
                </li>
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="text-red-700 mr-3 text-xl font-bold">✗</span>
                  <span><strong>Overpay for AI tools</strong> and digital services</span>
                </li>
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="text-red-700 mr-3 text-xl font-bold">✗</span>
                  <span><strong>Overpay simply because</strong> no one showed you another option</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-black/10 backdrop-blur-sm p-8 rounded-lg flex flex-col">
              <p className="text-2xl font-extrabold mb-6 text-black">
                This video doesn't "motivate" you.
              </p>
              <p className="text-lg leading-relaxed mb-4 text-black">
                It <strong className="text-white bg-black px-2 py-1 rounded">exposes</strong> a pricing layer most people never see.
              </p>
              <p className="text-lg leading-relaxed mb-4 text-black">
                Once you understand it, you'll <strong>immediately recognize</strong> where you've been paying more than necessary.
              </p>
              <p className="text-lg leading-relaxed mb-6 text-black">
                And you'll discover <strong className="underline decoration-2">exactly why</strong>.
              </p>
              <div className="mt-auto p-4 bg-black text-amber-400 rounded-lg text-center">
                <p className="font-bold text-lg">Average Savings: $3,200/year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Light Section with Three Columns */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20background%20with%20soft%20geometric%20shapes%20and%20subtle%20gradients%20in%20light%20gray%20and%20white%20tones%2C%20modern%20professional%20business%20aesthetic%2C%20clean%20and%20sophisticated%20design%20with%20gentle%20lighting%2C%20perfect%20for%20decision-making%20and%20choice%20concepts%2C%20high-end%20commercial%20photography%20style%20with%20copy%20space&width=1920&height=800&seq=is-this-for-you-bg-001&orientation=landscape"
            alt="Background"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Is This Video For You?
          </h2>
          <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto items-stretch">
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-red-500 flex flex-col">
              <div className="text-5xl mb-4 text-center">❌</div>
              <p className="text-2xl font-bold text-gray-900 mb-6 text-center">
                NOT For You If:
              </p>
              <ul className="space-y-4 text-base text-gray-700 flex-1">
                <li className="flex items-start">
                  <span className="mr-3 text-red-500 font-bold">✗</span>
                  <span>You prefer budgeting and cutting back</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-red-500 font-bold">✗</span>
                  <span>You believe retail pricing is unavoidable</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-red-500 font-bold">✗</span>
                  <span>You aren't curious how systems really work</span>
                </li>
              </ul>
              <p className="text-base text-gray-700 mt-6 font-semibold text-center bg-gray-100 p-3 rounded">
                If that's you, don't watch it!
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-8 rounded-lg shadow-xl flex flex-col">
              <div className="text-5xl mb-4 text-center">✅</div>
              <p className="text-2xl font-bold text-black mb-6 text-center">
                PERFECT For You If:
              </p>
              <ul className="space-y-4 text-base text-black flex-1">
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="mr-3 text-green-700 font-bold text-xl">✓</span>
                  <span>You're tired of overpaying</span>
                </li>
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="mr-3 text-green-700 font-bold text-xl">✓</span>
                  <span>You want insider access</span>
                </li>
                <li className="flex items-start bg-white/30 p-3 rounded-lg">
                  <span className="mr-3 text-green-700 font-bold text-xl">✓</span>
                  <span>You're ready to learn the system</span>
                </li>
              </ul>
              <p className="text-xl font-extrabold text-black mt-6 text-center">
                Watch It NOW!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-12 text-amber-400">What Others Are Saying</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gray-800 p-6 rounded-lg border-l-4 border-amber-400">
              <p className="text-gray-300 mb-4 italic">"I saved $4,200 on travel alone this year. This changed everything."</p>
              <p className="text-amber-400 font-bold">— Sarah M., Business Owner</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg border-l-4 border-amber-400">
              <p className="text-gray-300 mb-4 italic">"Finally understand why I was overpaying. Wish I found this sooner!"</p>
              <p className="text-amber-400 font-bold">— James T., Entrepreneur</p>
            </div>
            <div className="bg-gray-800 p-6 rounded-lg border-l-4 border-amber-400">
              <p className="text-gray-300 mb-4 italic">"This isn't a hack—it's how smart people have been doing it all along."</p>
              <p className="text-amber-400 font-bold">— Maria L., Consultant</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-700 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-block bg-red-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
              ⏰ LIMITED TIME: Access Closes Soon
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Get Instant Access to the Private Video
            </h2>
            <p className="text-xl text-gray-200 mb-4">
              Enter your name and email below and I'll send you the link <strong className="text-amber-400">immediately</strong>.
            </p>
            <p className="text-lg text-amber-400 mb-8 font-semibold">
              🎁 100% Free • No Credit Card • Instant Delivery
            </p>
            
            <form 
              id="aweber-form"
              data-readdy-form
              action="https://www.aweber.com/scripts/addlead.pl"
              method="POST"
              className="max-w-md mx-auto space-y-5 bg-white/10 backdrop-blur-sm p-8 rounded-xl border-2 border-amber-400"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const name = formData.get('name');
                const email = formData.get('email');
                
                // Submit to AWeber
                fetch('https://www.aweber.com/scripts/addlead.pl', {
                  method: 'POST',
                  body: new URLSearchParams({
                    'listname': 'awlist6939540',
                    'name': name as string,
                    'email': email as string,
                    'redirect': 'https://cryptolifestyle.me',
                    'meta_message': '1',
                    'meta_required': 'name,email',
                    'meta_adtracking': 'awlist6939540'
                  }),
                  mode: 'no-cors'
                }).then(() => {
                  // Redirect after submission
                  window.location.href = 'https://cryptolifestyle.me';
                }).catch(() => {
                  // Still redirect even if there's an error
                  window.location.href = 'https://cryptolifestyle.me';
                });
              }}
            >
              <input type="hidden" name="listname" value="awlist6939540" />
              <input type="hidden" name="redirect" value="https://cryptolifestyle.me" />
              <input type="hidden" name="meta_message" value="1" />
              <input type="hidden" name="meta_required" value="name,email" />
              <input type="hidden" name="meta_adtracking" value="awlist6939540" />
              
              <input
                type="text"
                name="name"
                placeholder="Enter Your First Name"
                required
                className="w-full px-6 py-5 rounded-lg text-base text-gray-900 focus:outline-none focus:ring-4 focus:ring-amber-400 font-medium"
              />
              <input
                type="email"
                name="email"
                placeholder="Enter Your Best Email"
                required
                className="w-full px-6 py-5 rounded-lg text-base text-gray-900 focus:outline-none focus:ring-4 focus:ring-amber-400 font-medium"
              />
              
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-black font-extrabold py-6 px-8 rounded-lg text-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-2xl border-4 border-black/20"
              >
                🔓 YES! Send Me The Video Now
              </button>

              <p className="text-xs text-gray-300 mt-4">
                🔒 Your information is 100% secure. We respect your privacy.
              </p>
            </form>

            <div className="mt-8 flex items-center justify-center gap-6 text-gray-300 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-green-500 text-xl">✓</span>
                <span>No Spam</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 text-xl">✓</span>
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 text-xl">✓</span>
                <span>100% Free</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Message Section */}
      <section className="py-16 bg-black text-white border-t-4 border-amber-400">
        <div className="container mx-auto px-4 text-center">
          <p className="text-2xl md:text-3xl mb-4 text-gray-300">
            You don't need more discipline.
          </p>
          <p className="text-3xl md:text-4xl font-extrabold text-amber-400">
            You need better access.
          </p>
          <p className="text-lg text-gray-400 mt-6 max-w-2xl mx-auto">
            The video above gives you exactly that. Don't wait—unlock it now before this page closes.
          </p>
        </div>
      </section>
    </div>
  );
}
